import Alpine from 'alpinejs';

import { isMotionFull, subscribeMotion } from '../motion';

/** Base delay in milliseconds per typed token at speed 1. Lower is faster. */
const BASE_DELAY_MS = 3;

const tokenizeHtml = (html: string): string[] => {
  const tokens: string[] = [];
  const regex = /(<[^>]+>)|([^<]+)/g;
  let match = regex.exec(html);

  while (match !== null) {
    if (match[1]) {
      tokens.push(match[1]);
    } else if (match[2]) {
      for (const char of match[2]) {
        tokens.push(char);
      }
    }
    match = regex.exec(html);
  }

  return tokens;
};

type TypingTextData = {
  displayed: string;
  tokens: string[];
  tokenIndex: number;
  started: boolean;
  frameId: number | null;
  delayMs: number;
  unsubscribe: (() => void) | null;
  init(this: TypingTextData & { $el: HTMLElement }): void;
  showStatic(this: TypingTextData & { $el: HTMLElement }): void;
  start(this: TypingTextData & { $el: HTMLElement }): void;
  destroy(this: TypingTextData): void;
};

export const registerTypingText = (): void => {
  Alpine.data(
    'typingText',
    (speed: number = 1): TypingTextData => ({
      displayed: '',
      tokens: [],
      tokenIndex: 0,
      started: false,
      frameId: null,
      delayMs: BASE_DELAY_MS / speed,
      unsubscribe: null,

      init() {
        const ghost = this.$el.querySelector('[data-typing-ghost]');
        const source = ghost?.innerHTML ?? '';
        this.tokens = tokenizeHtml(source);

        const applyTier = (): void => {
          if (!isMotionFull()) {
            this.showStatic();
          }
        };

        applyTier();
        this.unsubscribe = subscribeMotion(applyTier);
      },

      showStatic() {
        const ghost = this.$el.querySelector('[data-typing-ghost]');
        const source = ghost?.innerHTML ?? '';
        this.displayed = source;
        this.tokenIndex = this.tokens.length;
        this.started = true;
        this.$el.setAttribute('data-typing-static', '');
        if (this.frameId !== null) {
          cancelAnimationFrame(this.frameId);
          this.frameId = null;
        }
      },

      start() {
        if (this.started) return;
        this.started = true;

        if (!isMotionFull()) {
          this.showStatic();
          return;
        }

        let credit = 0;
        let lastTime: number | null = null;

        const animate = (time: number) => {
          if (!isMotionFull()) {
            this.showStatic();
            return;
          }

          if (lastTime === null) {
            lastTime = time;
          }

          credit += (time - lastTime) / this.delayMs;
          lastTime = time;

          const tokensToAdd = Math.min(
            Math.floor(credit),
            this.tokens.length - this.tokenIndex,
          );

          if (tokensToAdd > 0) {
            credit -= tokensToAdd;
            let chunk = '';
            for (let i = 0; i < tokensToAdd; i += 1) {
              chunk += this.tokens[this.tokenIndex + i];
            }
            this.displayed += chunk;
            this.tokenIndex += tokensToAdd;
          }

          if (this.tokenIndex < this.tokens.length) {
            this.frameId = requestAnimationFrame(animate);
          } else {
            this.frameId = null;
          }
        };

        this.frameId = requestAnimationFrame(animate);
      },

      destroy() {
        this.unsubscribe?.();
        this.unsubscribe = null;
        if (this.frameId !== null) {
          cancelAnimationFrame(this.frameId);
        }
      },
    }),
  );
};
